import { IAuth, INVALID_USER, User } from 'utils/models';
import axios, { AxiosResponse, CancelToken } from 'axios';
import React from 'react';
import { SweetAlertConst, patchApiResponse, processApiResponse } from 'utils';
import {
  ErrorDto,
  LoginDto,
  LoginParamDto,
  SeriLogDto,
} from 'utils/models/login.model';
import {
  AUTH,
  CREATE_LOG,
  GET_USER_ACCESS_TOKEN,
  REMOVE_FILE,
  UPLOAD_FILE,
} from './apiEndPoints';
import jwt_decode from 'jwt-decode';
import SweetAlert from 'components/atoms/sweetAlert';
import { SweetAlertEnums } from 'utils/enums';
import { useTranslation } from 'react-i18next';
import {
  getUploadFileError,
  getUploadRejectReason,
  showUploadRejections,
} from 'utils/fileUpload';
axios.defaults.headers.common = {
  'Access-Control-Allow-Origin': '*',
};

export const AuthContext = React.createContext<IAuth>({
  user: {} as User,
  isAuthenticated: false,
  isAdmin: function (): boolean {
    throw new Error('Function not implemented.');
  },
  logoutUser: function (): Promise<User | undefined | any> {
    throw new Error('Function not implemented.');
  },
  AuthenticateUser: function (value: boolean) {
    throw new Error('Function not implemented.');
  },
  setUserInfo: function (authToken: string): Promise<User | undefined | any> {
    throw new Error('Function not implemented.');
  },
  setUserPermission: function (
    authToken: string,
    permissions: any[]
  ): Promise<User | undefined | any> {
    throw new Error('Function not implemented.');
  },
  authWithToken: function (
    token?: string | undefined
  ): Promise<User | undefined | any> {
    throw new Error('Function not implemented.');
  },
  makeAuthenticatedGet: function (
    endpoint: string,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<any, any>> {
    throw new Error('Function not implemented.');
  },
  makeAuthenticatedPost: function (
    endpoint: string,
    data: any,
    customHeaders?: object | undefined
  ): Promise<AxiosResponse<any, any>> {
    throw new Error('Function not implemented.');
  },
  makeAuthenticatedPut: function (
    endpoint: string,
    data: any,
    customHeaders?: object | undefined
  ): Promise<AxiosResponse<any, any>> {
    throw new Error('Function not implemented.');
  },
  makeAuthenticatedDelete: function (
    endpoint: string
  ): Promise<AxiosResponse<any, any>> {
    throw new Error('Function not implemented.');
  },
  makeAuthenticatedPatch: function (
    endpoint: string,
    data: any,
    customHeaders?: object | undefined
  ): Promise<AxiosResponse<any, any>> {
    throw new Error('Function not implemented.');
  },
  loginUser: function (param: LoginParamDto): Promise<LoginDto> {
    throw new Error('Function not implemented.');
  },
  FileUpload: function (file: any, directory: string): Promise<AxiosResponse> {
    throw new Error('Function not implemented.');
  },
  RemoveFile: function (file: string): Promise<boolean> {
    throw new Error('Function not implemented.');
  },
  CreateLog: function (data: SeriLogDto): Promise<AxiosResponse> {
    throw new Error('Function not implemented.');
  },
});

export function useProvideAuth() {
  const { i18n } = useTranslation();

  const [user, setUser] = React.useState<User>(INVALID_USER);
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);

  const interceptors = axios.interceptors.request.use(
    (config) => {
      const token: string = localStorage.getItem('access-token') || '';
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const logoutUser = async () => {
    localStorage.clear();
    setUser(INVALID_USER);
    setIsAuthenticated(false);
  };

  const AuthenticateUser = (value: boolean) => {
    setIsAuthenticated(value);
  };

  const isAdmin = () => {
    var admin = user.Roles.some(
      (obj: any) => obj?.Id === 1 || obj?.Name?.toLowerCase().includes('admin')
    );
    return admin;
  };

  const setUserInfo = async (authToken: string) => {
    var decoded: any = jwt_decode(authToken);
    const userData = {
      email: decoded?.StaffEmail || '',
      firstName: decoded?.StaffName?.split(' ')[0] || '',
      fullName: decoded?.StaffName || '',
      userName: decoded?.UserName || '',
      id: (localStorage.getItem('loggedInUserId') as any) || '',
      lastName: decoded?.StaffName?.split(' ')[1] || '',
      nickname: '',
      pictureUrl: '',
      RoleId: decoded?.RoleId || '',
      RoleName: decoded?.RoleName || '',
      authToken: authToken,
      Roles: JSON.parse(decoded?.Roles),
      Permissions: [],
      DefaultLang: decoded?.DefaultLang,
    };

    let multiLoginUser: any = localStorage.getItem('multiLoginUser');
    multiLoginUser = multiLoginUser ? JSON.parse(multiLoginUser) : [];
    const userNameExists = multiLoginUser.some(
      (user: any) => user.userName === decoded.UserName
    );

    !userNameExists &&
      multiLoginUser.push({
        staffName: decoded.StaffName,
        staffId: localStorage.getItem('loggedInUserId') || '',
        userName: decoded.UserName,
      });
    localStorage.setItem('multiLoginUser', JSON.stringify(multiLoginUser));

    i18n.changeLanguage(decoded?.DefaultLang);
    setUser(userData);
    return userData;
  };
  const setUserPermission = async (authToken: string, permissions: any[]) => {
    var decoded: any = jwt_decode(authToken);
    const userData = {
      email: decoded?.StaffEmail || '',
      firstName: decoded?.StaffName?.split(' ')[0] || '',
      fullName: decoded?.StaffName || '',
      userName: decoded?.UserName || '',
      id: (localStorage.getItem('loggedInUserId') as any) || '',
      lastName: decoded?.StaffName?.split(' ')[1] || '',
      nickname: '',
      pictureUrl: '',
      RoleId: decoded?.RoleId || '',
      RoleName: decoded?.RoleName || '',
      authToken: authToken,
      Roles: JSON.parse(decoded?.Roles),
      Permissions: permissions,
      DefaultLang: decoded?.DefaultLang || '',
    };
    i18n.changeLanguage(decoded?.DefaultLang);
    setUser(userData);
    return userData;
  };

  const authWithToken = async (authToken?: string) => {
    if (authToken === undefined) {
      const token = localStorage.getItem('access-token');
      if (token) {
        authToken = token;
      }
    }

    if (authToken) {
      try {
        localStorage.setItem('access-token', authToken);

        setUserInfo(authToken);
        return '';
      } catch (error) {
        console.error(error);
        setUser(INVALID_USER);
        // setAuthenticatedConn(null);
        throw new Error('Token invalid / expired');
      }
    }

    return undefined;
  };

  const makeAuthenticatedGet = async (
    endpoint: string,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse> => {
    try {
      const result = await axios.get(endpoint, {
        baseURL: process.env.REACT_APP_PUBLIC_API_URL,
        headers: {
          //   Authorization: "Bearer " + user.authToken
        },
        cancelToken,
      });
      return Promise.resolve(result);
    } catch (error: any) {
      if (error.code === 'ERR_CANCELED') {
        return Promise.reject(error.code);
      }
      SweetAlert({
        showSweetAlert: true,
        sweetAlertType: SweetAlertEnums.error,
        title: SweetAlertConst.error,
        subTitle:
          error?.response?.data.error_description ||
          SweetAlertConst.somethingWentWrong,
        timeOut: 10000,
      });
      console.error(`[makeAuthenticatedRequest] URL: ${endpoint}`);
      console.error('Request error', { error });
      if (error.request) {
        // The server responded with an error
        if (error.request.status === 401) {
          // We got an unauthorized response and should try to reauth
          // TODO(Brendan): Reauth?
          return Promise.reject('Unauthorized');
        } else if (error.response.status === 500) {
          if (error.response.data.error) {
            const errorData: ErrorDto = error.response.data;
            console.error('Unknown error from server: ', { errorData });
            return Promise.reject(errorData);
          }
        }
        console.error('Unknown error from server: ', error);
        console.error(error.response);
      } else {
        // The server did not respond
        console.error({ error });
        return Promise.reject('Timeout');
      }
    }

    return Promise.reject('Unknown error');
  };

  const makeAuthenticatedPost = async (
    endpoint: string,
    data: any,
    customHeaders?: object
  ): Promise<AxiosResponse> => {
    try {
      const result = await axios.post(endpoint, data, {
        baseURL: process.env.REACT_APP_PUBLIC_API_URL,
        headers: {
          // Authorization: "Bearer " + user.authToken,
          ...customHeaders,
        },
      });
      return Promise.resolve(result);
    } catch (error: any) {
      // console.error('Request error', { error });
      SweetAlert({
        showSweetAlert: true,
        sweetAlertType: SweetAlertEnums.error,
        title: SweetAlertConst.error,
        subTitle:
          error.response?.data?.error_description ||
          (error.response?.status === 400 &&
          typeof error.response?.data === 'string'
            ? error.response.data
            : SweetAlertConst.somethingWentWrong),
        timeOut: 10000,
      });
      if (error.response) {
        // The server responded with an error
        if (error.response.status === 401) {
          // We got an unauthorized response and should try to reauth
          // TODO(Brendan): Reauth?
          return Promise.reject('Unauthorized');
        } else if (error.response.status === 500) {
          if (error.response.data.error) {
            const errorData: ErrorDto = error.response.data;
            console.error('Unknown error from server: ', { errorData });
            return Promise.reject(errorData);
          }
        }
        console.error('Unknown error from server: ', { error });
        return Promise.reject(error);
        // console.error(error.response);
      } else {
        // The server did not respond
        return Promise.reject('Timeout');
      }
    }
  };
  const makeAuthenticatedPut = async (
    endpoint: string,
    data: any,
    customHeaders?: object
  ): Promise<AxiosResponse> => {
    try {
      const result = await axios.put(endpoint, data, {
        baseURL: process.env.REACT_APP_PUBLIC_API_URL,
        headers: {
          // Authorization: "Bearer " + user.authToken,
          ...customHeaders,
        },
      });
      return Promise.resolve(processApiResponse(result));
    } catch (error: any) {
      SweetAlert({
        showSweetAlert: true,
        sweetAlertType: SweetAlertEnums.error,
        title: SweetAlertConst.error,
        subTitle:
          error?.response?.data.error_description ||
          SweetAlertConst.somethingWentWrong,
        timeOut: 10000,
      });
      // console.error('Request error', { error });
      if (error.response) {
        // The server responded with an error
        if (error.response.status === 401) {
          // We got an unauthorized response and should try to reauth
          // TODO(Brendan): Reauth?
          return Promise.reject('Unauthorized');
        } else if (error.response.status === 500) {
          if (error.response.data.error) {
            const errorData: ErrorDto = error.response.data;
            console.error('Unknown error from server: ', { errorData });
            return Promise.reject(errorData);
          }
        }
        console.error('Unknown error from server: ', { error });
        // console.error(error.response);
      } else {
        // The server did not respond
        return Promise.reject('Timeout');
      }
    }

    return Promise.reject('Unknown error');
  };

  const makeAuthenticatedPatch = async (
    endpoint: string,
    data: any,
    customHeaders?: object
  ): Promise<AxiosResponse> => {
    try {
      const result = await axios.patch(endpoint, data, {
        baseURL: process.env.REACT_APP_PUBLIC_API_URL,
        headers: {
          // Authorization: "Bearer " + user.authToken,
          ...customHeaders,
          'Content-Type': 'application/json-patch+json',
        },
      });
      return Promise.resolve(patchApiResponse(result));
    } catch (error: any) {
      SweetAlert({
        showSweetAlert: true,
        sweetAlertType: SweetAlertEnums.error,
        title: SweetAlertConst.error,
        subTitle:
          error?.response?.data.error_description ||
          SweetAlertConst.somethingWentWrong,
        timeOut: 10000,
      });
      // console.error('Request error', { error });
      if (error.response) {
        // The server responded with an error
        if (error.response.status === 401) {
          // We got an unauthorized response and should try to reauth
          // TODO(Brendan): Reauth?
          return Promise.reject('Unauthorized');
        } else if (error.response.status === 500) {
          if (error.response.data.error) {
            const errorData: ErrorDto = error.response.data;
            console.error('Unknown error from server: ', { errorData });
            return Promise.reject(errorData);
          }
        }
        console.error('Unknown error from server: ', { error });
        // console.error(error.response);
      } else {
        // The server did not respond
        return Promise.reject('Timeout');
      }
    }

    return Promise.reject('Unknown error');
  };
  const makeAuthenticatedDelete = async (
    endpoint: string
  ): Promise<AxiosResponse> => {
    try {
      const result = await axios.delete(endpoint, {
        baseURL: process.env.REACT_APP_PUBLIC_API_URL,
        headers: {
          // Authorization: "Bearer " + user.authToken,
        },
      });
      return Promise.resolve(result);
    } catch (error: any) {
      SweetAlert({
        showSweetAlert: true,
        sweetAlertType: SweetAlertEnums.error,
        title: SweetAlertConst.error,
        subTitle:
          error?.response?.data.error_description ||
          SweetAlertConst.somethingWentWrong,
        timeOut: 10000,
      });

      console.error(`[makeAuthenticatedRequest] URL: ${endpoint}`);
      console.error('Request error', { error });
      if (error.request) {
        // The server responded with an error
        if (error.request.status === 401) {
          // We got an unauthorized response and should try to reauth
          // TODO(Brendan): Reauth?
          return Promise.reject('Unauthorized');
        } else if (error.response.status === 500) {
          if (error.response.data.error) {
            const errorData: ErrorDto = error.response.data;
            console.error('Unknown error from server: ', { errorData });
            return Promise.reject(errorData);
          }
        }
        console.error('Unknown error from server: ', error);
        console.error(error.response);
      } else {
        // The server did not respond
        console.error({ error });
        return Promise.reject('Timeout');
      }
    }

    return Promise.reject('Unknown error');
  };

  // logins
  const loginUser = async (param: LoginParamDto): Promise<LoginDto> => {
    try {
      let res: any = await makeAuthenticatedPost(
        AUTH + GET_USER_ACCESS_TOKEN,
        param
      );
      return Promise.resolve(res.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  // file upload
  const FileUpload = async (file: any, directory: string = '') => {
    const fileError = getUploadFileError(file);
    if (fileError) {
      showUploadRejections([
        { name: file?.name || '', reason: getUploadRejectReason(file) || '' },
      ]);
      return Promise.reject(new Error(fileError));
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      // Upload endpoint requires a staff login - send the token explicitly
      const token = localStorage.getItem('access-token') || '';
      let res: any = await makeAuthenticatedPost(
        AUTH + UPLOAD_FILE + '?directory=' + directory,
        formData,
        { Authorization: `Bearer ${token}` }
      );
      return Promise.resolve(res.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const RemoveFile = async (file: string): Promise<boolean> => {
    try {
      let res: any = await makeAuthenticatedPost(
        AUTH + REMOVE_FILE + '?fileUrl=' + file,
        {}
      );
      return Promise.resolve(res.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  // Log
  const CreateLog = async (data: SeriLogDto) => {
    try {
      let res: any = await makeAuthenticatedPost(AUTH + CREATE_LOG, data);
      return Promise.resolve(res.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  // Return the user object and auth methods
  return {
    user,
    isAuthenticated,
    authWithToken,
    makeAuthenticatedGet,
    makeAuthenticatedPost,
    makeAuthenticatedDelete,
    makeAuthenticatedPut,
    makeAuthenticatedPatch,
    loginUser,
    logoutUser,
    setUserInfo,
    setUserPermission,
    AuthenticateUser,
    isAdmin,
    FileUpload,
    RemoveFile,
    CreateLog,
  };
}

export function useAuth() {
  return React.useContext(AuthContext);
}
